// ============================================
// MEDFLOW - Voice Chat Hook (STT + TTS)
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import { voiceApi } from '@/lib/api';

interface UseVoiceChatReturn {
  isSTTSupported: boolean;
  isVoiceMode: boolean;
  toggleVoiceMode: () => void;
  isListening: boolean;
  interimTranscript: string;
  startListening: () => void;
  stopListening: () => void;
  isSpeaking: boolean;
  speakText: (text: string) => Promise<void>;
  stopSpeaking: () => void;
}

export function useVoiceChat(): UseVoiceChatReturn {
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const SpeechRecognitionClass =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const isSTTSupported = !!SpeechRecognitionClass;

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const toggleVoiceMode = useCallback(() => {
    setIsVoiceMode((prev) => {
      if (prev) {
        // Turning off — stop everything
        if (recognitionRef.current) {
          recognitionRef.current.abort();
          recognitionRef.current = null;
        }
        setIsListening(false);
        setInterimTranscript('');
        cleanupAudio();
        setIsSpeaking(false);
      }
      return !prev;
    });
  }, [cleanupAudio]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionClass) return;

    // Stop any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.lang = 'es-CL';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setInterimTranscript('');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          // Final result — will be handled by the component via onFinalTranscript
          setInterimTranscript(result[0].transcript);
        } else {
          interim += result[0].transcript;
        }
      }
      if (interim) {
        setInterimTranscript(interim);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'no-speech' and 'aborted' are expected, not real errors
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('Speech recognition error:', event.error);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch {
      console.warn('Speech recognition failed to start');
      setIsListening(false);
    }
  }, [SpeechRecognitionClass]);

  const stopSpeaking = useCallback(() => {
    cleanupAudio();
    setIsSpeaking(false);
  }, [cleanupAudio]);

  const speakText = useCallback(
    async (text: string) => {
      // Stop any current audio
      cleanupAudio();

      try {
        setIsSpeaking(true);
        const blob = await voiceApi.synthesize(text);
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.onended = () => {
          setIsSpeaking(false);
          cleanupAudio();
        };

        audio.onerror = () => {
          setIsSpeaking(false);
          cleanupAudio();
        };

        await audio.play();
      } catch (err) {
        console.warn('TTS synthesis failed:', err);
        setIsSpeaking(false);
        cleanupAudio();
      }
    },
    [cleanupAudio]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  return {
    isSTTSupported,
    isVoiceMode,
    toggleVoiceMode,
    isListening,
    interimTranscript,
    startListening,
    stopListening,
    isSpeaking,
    speakText,
    stopSpeaking,
  };
}
